import { getMomentTz, TimeService } from "@dexteel/mesf-core";
import {
	Autocomplete,
	Box,
	Chip,
	Grid2,
	MenuItem,
	TextField,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { Controller, type UseFormReturn } from "react-hook-form";
import { CheckboxTextField } from "@/controls/CheckboxTextField";
import { AREA_COLOR_MAP } from "@/shared/colors";
import type { GroupedDelayAsset } from "@/shared/repositories/DelayAssetRepository";
import { EVENT_TYPES } from "../config/eventTypes";
import type { EventForm } from "./EventEditorModal";

const moment = getMomentTz();
type Props = {
	form: UseFormReturn<EventForm>;
	assets: GroupedDelayAsset[];
	isEdit: boolean;
	isWindowLocked: boolean;
	workOrdersOnly: boolean;
	blockerText: string | null;
};
export const EventScheduleFields = ({
	form,
	assets,
	isEdit,
	isWindowLocked,
	workOrdersOnly,
	blockerText,
}: Props) => {
	const { control, watch, getValues } = form;
	const eventType = watch("EventTypeCode");
	const serverTz = TimeService.getInstance().getServerTimeZone();
	return (
		<>
			{!workOrdersOnly && (
				<Grid2 size={{ xs: 12, md: 4 }}>
					<Controller
						name="EventTypeCode"
						control={control}
						render={({ field }) => (
							<TextField
								select
								label="Event Type"
								value={field.value}
								onChange={field.onChange}
								size="small"
								fullWidth
								disabled={isEdit}
							>
								{EVENT_TYPES.map((t) => (
									<MenuItem key={t.code} value={t.code}>
										<Box
											component="span"
											sx={{
												display: "inline-block",
												width: 10,
												height: 10,
												borderRadius: "50%",
												backgroundColor: t.color,
												mr: 1,
											}}
										/>
										{t.name}
									</MenuItem>
								))}
							</TextField>
						)}
					/>
				</Grid2>
			)}
			{(["Start", "End"] as const).map((name) => (
				<Grid2 key={name} size={{ xs: 6, md: 4 }}>
					<Controller
						name={name}
						control={control}
						rules={{
							required: `${name} is required`,
							validate: (value) =>
								name !== "End" ||
								!value ||
								!getValues("Start") ||
								value > getValues("Start")! ||
								"End must be after Start",
						}}
						render={({ field, fieldState: { error } }) => (
							<DateTimePicker
								label={name}
								timezone={serverTz}
								format="MM/DD/YY HH:mm"
								ampm={false}
								disabled={isWindowLocked}
								value={field.value ? moment(field.value) : null}
								onChange={(value) => field.onChange(value?.toDate() ?? null)}
								minDateTime={
									name === "End" && getValues("Start")
										? moment(getValues("Start"))
										: undefined
								}
								slotProps={{
									textField: {
										size: "small",
										fullWidth: true,
										error: !!error || !!blockerText,
										helperText: error?.message ?? blockerText,
									},
								}}
							/>
						)}
					/>
				</Grid2>
			))}

			{(eventType === "M" || eventType === "D" || eventType === "B") && (
				<Grid2 size={{ xs: 12, md: 8 }}>
					<Controller
						name="AssetIds"
						control={control}
						rules={{
							validate: (v) => v.length > 0 || "Select at least one line",
						}}
						render={({ field, fieldState: { error } }) => (
							<Autocomplete
								multiple
								disableCloseOnSelect
								disabled={isEdit}
								size="small"
								options={assets ?? []}
								getOptionLabel={(o) => o.AssetName}
								value={(assets ?? []).filter((a) =>
									field.value.includes(a.AssetId),
								)}
								isOptionEqualToValue={(o, v) => o.AssetId === v.AssetId}
								onChange={(_, v) => field.onChange(v.map((a) => a.AssetId))}
								renderTags={(value, getTagProps) =>
									value.map((option, index) => {
										const color =
											AREA_COLOR_MAP[option.AssetId] ??
											AREA_COLOR_MAP[option.ParentAssetId ?? 0] ??
											"#bdbdbd";
										return (
											<Chip
												{...getTagProps({ index })}
												key={option.AssetId}
												size="small"
												label={option.AssetName}
												sx={{
													backgroundColor: color,
													color: "#fff",
													fontWeight: 500,
													"& .MuiChip-deleteIcon": {
														color: "#FFFFFFCC",
													},
												}}
											/>
										);
									})
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Lines"
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						)}
					/>
				</Grid2>
			)}

			{eventType === "H" && (
				<Grid2 size={{ xs: 12, md: 8 }}>
					<TextField
						label="Scope"
						value="Whole plant"
						size="small"
						fullWidth
						disabled
					/>
				</Grid2>
			)}

			{eventType === "B" && (
				<Grid2 size={12}>
					<Controller
						name="IsSpell"
						control={control}
						render={({ field }) => (
							<CheckboxTextField
								label="Spell — crew rotates, the line keeps running"
								checked={field.value ?? false}
								onChange={field.onChange}
							/>
						)}
					/>
				</Grid2>
			)}

			{eventType !== "W" && (
				<Grid2 size={12}>
					<Controller
						name="Comments"
						control={control}
						render={({ field }) => (
							<TextField
								label="Comments"
								value={field.value}
								onChange={field.onChange}
								multiline
								minRows={2}
								size="small"
								fullWidth
								slotProps={{ htmlInput: { maxLength: 500 } }}
							/>
						)}
					/>
				</Grid2>
			)}
		</>
	);
};
